import { createElement } from '../utils/dom.js';
import { iconX } from '../utils/icons.js';

/** @type {HTMLElement|null} */
let currentModal = null;

/** @type {Function|null} */
let currentOnClose = null;

/**
 * Ferme la modale active avec animation.
 */
export function closeModal() {
  if (!currentModal) return;

  const modal = currentModal;
  const onClose = currentOnClose;

  currentModal = null;
  currentOnClose = null;

  modal.classList.remove('modal--open');

  let closed = false;
  const finishClose = () => {
    if (closed) return;
    closed = true;
    modal.remove();
    document.removeEventListener('keydown', handleEscape);
    onClose?.();
  };

  // Fallback si pas de transition CSS
  setTimeout(finishClose, 350);
  modal.addEventListener('transitionend', finishClose, { once: true });
}

/**
 * Gestionnaire Escape pour fermer la modale.
 * @param {KeyboardEvent} e
 */
function handleEscape(e) {
  if (e.key === 'Escape') {
    closeModal();
  }
}

/**
 * Ouvre une modale avec titre, contenu et pied de page optionnels.
 * @param {{ title: string, content: HTMLElement|string, footer?: HTMLElement, onClose?: Function }} options
 * @returns {{ modal: HTMLElement, body: HTMLElement }}
 */
export function openModal({ title, content, footer, onClose }) {
  closeModal();

  const body = createElement('div', { className: 'modal__body' });

  if (typeof content === 'string') {
    body.textContent = content;
  } else {
    body.appendChild(content);
  }

  const closeBtn = createElement('button', {
    className: 'modal__close',
    attrs: { type: 'button', 'aria-label': 'Fermer' },
    children: [iconX(20)],
    events: { click: closeModal },
  });

  const header = createElement('div', {
    className: 'modal__header',
    children: [
      createElement('h2', { className: 'modal__title', text: title }),
      closeBtn,
    ],
  });

  const contentEl = createElement('div', { className: 'modal__content' });
  contentEl.appendChild(header);
  contentEl.appendChild(body);

  if (footer) {
    const footerEl = createElement('div', { className: 'modal__footer' });
    footerEl.appendChild(footer);
    contentEl.appendChild(footerEl);
  }

  const overlay = createElement('div', {
    className: 'modal__overlay',
    events: { click: closeModal },
  });

  const modal = createElement('div', {
    className: 'modal',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'modal-title' },
    children: [overlay, contentEl],
  });

  document.body.appendChild(modal);
  currentModal = modal;
  currentOnClose = onClose ?? null;

  document.addEventListener('keydown', handleEscape);

  // Animation d'ouverture
  requestAnimationFrame(() => {
    modal.classList.add('modal--open');
  });

  return { modal, body };
}
